import NoteActions from 'actions/NoteActions';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import shallowCopyNote from 'notebook/notes/shallowCopyNote';
import notifications from 'notifications';
import {UpdateMixtureParams} from 'registry/mixtures/MixtureConstants';
import * as MixtureDAO from 'registry/mixtures/MixtureDAO';
import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';

import {UpdateMixtureResponseGraphQL} from '../../../graphql-fragments/registry/mixtures/__generated/UpdateMixtureResponseGraphQL';

export async function update(
  mixtureId: string,
  data: UpdateMixtureParams,
  dataModsHeader?: DataModificationsHeader
): Promise<UpdateMixtureResponseGraphQL> {
  const response = await MixtureDAO.update(mixtureId, data, dataModsHeader);
  writeFragmentsToCache(getFragmentWrapper('UpdateMixtureResponse'), [response.mixture]);
  notifications.success('Successfully updated mixture');
  return response.mixture;
}

export async function updateWithDataMods(
  mixtureId: string,
  isRegistered: boolean,
  data: UpdateMixtureParams
): Promise<UpdateMixtureResponseGraphQL | null> {
  let mixture: UpdateMixtureResponseGraphQL | null = null;
  await callAsyncWithDataModifications({
    changeSummary: isRegistered
      ? getInventoryRegistryChangeSummary({
          apiIds: [mixtureId],
          action: InventoryRegistryDataModAction.MODIFY,
        })
      : '',
    onPerformAction: async (dataModsHeader?: DataModificationsHeader) => {
      mixture = await update(mixtureId, data, dataModsHeader);
    },
    apiIds: [mixtureId],
    analyticsParams: {
      module: 'REGISTRY',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.MODIFY,
      numModifiedTotalItems: 1,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY && isRegistered,
  });
  return mixture;
}

export async function initializeInstructions(mixtureId: string): Promise<void> {
  const response = await MixtureDAO.initializeInstructions(mixtureId);
  const mixtureResponse = response.mixture;
  assert(
    mixtureResponse.instructions != null,
    `Expected initialize-instructions endpoint to create non-null instructions for mixture ${mixtureResponse.id}`
  );
  NoteActions.load(
    mixtureResponse.instructions.id,
    shallowCopyNote(mixtureResponse.instructions.noteJson)
  ).done(() => {
    // Do this in the callback to ensure that the MixtureInstructions component doesn't re-render until the
    // NoteStore has been updated with the initial note version for the new instructions note
    writeFragmentsToCache(getFragmentWrapper('MixtureWithInstructions'), [mixtureResponse]);
  });
}
